import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { colors } from './TabNavigation';
import useIsMobile from './useIsMobile';
import Sidebar from './Sidebar';
import BirthChartForm from './BirthChartForm';
import ChartDisplay from './ChartDisplay';
import { LogoShowcase } from './Logo';
import Landing from './Landing';
import Footer from './Footer';
import { PrivacyPolicy, Disclaimer, TermsOfUse } from './LegalPages';
import { Sentry } from './sentry';
import {
  getLastChart, setLastChart,
  getSavedCharts, saveChart, deleteSavedChart,
  chartKey, defaultLabel,
} from './savedCharts';

// Lazy-loaded so the ~4MB of transit/synastry interpretation data these pull in
// is fetched only when the user opens that tab — keeping first load light.
const TransitCalendar = lazy(() => import('./TransitCalendar'));
const TransitYearCalendar = lazy(() => import('./TransitYearCalendar'));
const Synastry = lazy(() => import('./Synastry'));

function App() {
  const isMobile = useIsMobile();
  const [birthChart, setBirthChart] = useState(() => getLastChart()?.chart || null);
  const [unknownTime, setUnknownTime] = useState(() => getLastChart()?.unknownTime || false);
  const [activeTab, setActiveTab] = useState('home');
  const [loading, setLoading] = useState(false);
  const [slowLoad, setSlowLoad] = useState(false);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const slowLoadTimer = useRef(null);

  // Local saved-charts library (browser only — no account needed)
  const [savedCharts, setSavedCharts] = useState(() => getSavedCharts());
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [saveLabel, setSaveLabel] = useState('');
  const [showSwitcher, setShowSwitcher] = useState(false);

  const currentKey = birthChart ? chartKey(birthChart) : null;
  const currentEntry = savedCharts.find((e) => e.id === currentKey);
  const currentSaved = !!currentEntry;

  const handleSaveChart = (label) => {
    const next = saveChart(birthChart, unknownTime, label);
    if (next) {
      setSavedCharts(next);
      setShowSaveInput(false);
      setSaveLabel('');
    }
  };

  const handleLoadChart = (entry) => {
    setBirthChart(entry.chart);
    setUnknownTime(!!entry.unknownTime);
    setLastChart(entry.chart, entry.unknownTime);
    setShowSaveInput(false);
    setShowSwitcher(false);
    setActiveTab('calculator');
    window.scrollTo({ top: 0 });
  };

  const handleDeleteChart = (id) => {
    setSavedCharts(deleteSavedChart(id));
  };

  useEffect(() => {
    if (loading) {
      slowLoadTimer.current = setTimeout(() => setSlowLoad(true), 5000);
    } else {
      clearTimeout(slowLoadTimer.current);
      setSlowLoad(false);
    }
    return () => clearTimeout(slowLoadTimer.current);
  }, [loading]);

  const handleChartCalculation = async (formData) => {
    setLoading(true);
    setError('');
    const controller = new AbortController();
    // Render's free tier can cold-start for ~50s; allow a generous window so we
    // don't abort a server that's simply waking up, but still fail eventually
    // instead of spinning forever on a truly hung request.
    const timeout = setTimeout(() => controller.abort(), 70000);
    try {
      const API = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL.trim() : 'http://localhost:5001';
      const response = await fetch(`${API}/api/birth-chart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
        signal: controller.signal,
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to calculate chart');
      }
      const chartData = await response.json();
      // The API's birthData omits the house system; attach it so a saved chart is
      // self-describing and can be keyed/distinguished by system (see savedCharts.chartKey).
      if (chartData && chartData.birthData && formData.houseSystem) {
        chartData.birthData.houseSystem = formData.houseSystem;
      }
      setUnknownTime(formData.unknownTime || false);
      setBirthChart(chartData);
      setLastChart(chartData, formData.unknownTime || false);
      setShowSaveInput(false);
      setShowSwitcher(false);
    } catch (err) {
      const msg = err.name === 'AbortError'
        ? 'The server took too long to respond — it may be waking up. Please try again in a moment.'
        : err.message === 'Failed to fetch'
        ? 'We couldn’t reach the server. Please check your connection and try again.'
        : err.message;
      setError(msg);
      console.error('Chart calculation error:', err);
      Sentry.captureException(err); // no-op unless Sentry is configured
    } finally {
      clearTimeout(timeout);
      setLoading(false);
    }
  };

  // Hero text colours (dark section — not using colors.* which targets cream bg)
  const heroText = {
    primary: '#f5ede3',
    secondary: 'rgba(245, 237, 227, 0.6)',
  };

  return (
    <div style={{ minHeight: '100vh' }}>

      {/* ── HERO — dark, cinematic ── */}
      <header style={{
        position: 'relative',
        minHeight: '68vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '80px 24px 100px',
        // Overlay goes near-opaque at the bottom so no photo bleeds through → clean cut to cream
        background: `
          radial-gradient(ellipse at 50% 75%, rgba(201, 149, 110, 0.22) 0%, transparent 50%),
          linear-gradient(to bottom, rgba(8, 9, 14, 0.15) 0%, rgba(8, 9, 14, 0.50) 45%, rgba(8, 9, 14, 0.88) 72%, rgba(8, 9, 14, 0.98) 100%),
          url('/hero-night-sky.jpg') center 38% / cover no-repeat,
          #0a0b12
        `,
      }}>
        {/* Hamburger Menu Button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          style={{
            position: 'absolute',
            top: '24px',
            right: '24px',
            fontSize: '24px',
            backgroundColor: 'transparent',
            border: 'none',
            color: heroText.secondary,
            cursor: 'pointer',
            padding: '8px',
            lineHeight: 1,
            transition: 'color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = heroText.primary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = heroText.secondary;
          }}
        >
          ☰
        </button>
        {/* Logo options — IZ to choose */}
        <LogoShowcase heroMode />

        <h1 style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: 'clamp(26px, 8vw, 38px)',
          fontWeight: '400',
          color: heroText.primary,
          letterSpacing: '10px',
          textTransform: 'uppercase',
          margin: '24px 0 16px',
        }}>
          Cuspli
        </h1>

        <p style={{
          fontSize: '17px',
          fontWeight: '300',
          fontStyle: 'italic',
          color: heroText.secondary,
          margin: 0,
          letterSpacing: '0.5px',
        }}>
          your cosmic blueprint
        </p>

        {/* Scroll cue */}
        <div style={{
          position: 'absolute',
          bottom: '36px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '6px',
        }}>
          <div style={{
            width: '1px',
            height: '36px',
            background: `linear-gradient(to bottom, rgba(201, 149, 110, 0.7), transparent)`,
          }} />
        </div>
      </header>

      {/* ── CONTENT — warm cream with hidden sidebar ── */}
      <main style={{
        backgroundColor: colors.creamBg,
        paddingTop: '72px',
        paddingBottom: '100px',
      }}>
          {/* Tab Content */}
          <Suspense fallback={
            <div style={{ textAlign: 'center', padding: '80px 24px', color: colors.textSecondary, fontSize: isMobile ? '16px' : '14px', fontStyle: 'italic', letterSpacing: '1px' }}>
              loading&hellip;
            </div>
          }>
          {activeTab === 'home' && (
            <Landing onNavigate={setActiveTab} />
          )}

          {activeTab === 'calculator' && (
            <div>
              {loading && (
                <div style={{
                  textAlign: 'center',
                  padding: '80px 24px',
                }}>
                  <div style={{
                    fontSize: isMobile ? '16px' : '14px',
                    fontStyle: 'italic',
                    color: colors.textSecondary,
                    letterSpacing: '1px',
                    marginBottom: '12px',
                  }}>
                    reading the stars&hellip;
                  </div>
                  {slowLoad && (
                    <div style={{
                      fontSize: isMobile ? '14px' : '12px',
                      color: colors.textMuted,
                      fontStyle: 'italic',
                      fontWeight: '300',
                      marginTop: '8px',
                    }}>
                      First load takes a little longer — the server is waking up. Usually under a minute.
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div style={{
                  maxWidth: '560px',
                  margin: '0 auto 32px',
                  padding: '20px 24px',
                  backgroundColor: 'rgba(180, 60, 60, 0.06)',
                  border: '1px solid rgba(180, 60, 60, 0.2)',
                  color: '#b04040',
                  fontSize: isMobile ? '16px' : '14px',
                  fontWeight: '300',
                  textAlign: 'center',
                  letterSpacing: '0.3px',
                }}>
                  {error}
                </div>
              )}

              {!birthChart && !loading && (
                <div>
                  {savedCharts.length > 0 && (
                    <div style={{ maxWidth: '520px', margin: '0 auto 48px', padding: '0 24px' }}>
                      <p style={{
                        fontSize: isMobile ? '12px' : '10px',
                        fontWeight: '500',
                        letterSpacing: '2.5px',
                        textTransform: 'uppercase',
                        color: colors.gold,
                        marginBottom: '16px',
                        textAlign: 'center',
                      }}>
                        Your saved charts
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', backgroundColor: colors.borderSubtle }}>
                        {savedCharts.map((entry) => (
                          <div
                            key={entry.id}
                            onClick={() => handleLoadChart(entry)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              backgroundColor: colors.darkSurface,
                              padding: '16px 18px',
                              cursor: 'pointer',
                              transition: 'background-color 0.2s ease',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.darkSurfaceHover}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.darkSurface}
                          >
                            <div>
                              <div style={{
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontSize: '17px',
                                color: colors.textPrimary,
                                lineHeight: '1.3',
                              }}>
                                {entry.label}
                              </div>
                              <div style={{ fontSize: isMobile ? '14px' : '12px', color: colors.textMuted, marginTop: '2px' }}>
                                {entry.chart.birthData.date} · {entry.chart.birthData.time}
                              </div>
                            </div>
                            <button
                              aria-label="Delete saved chart"
                              onClick={(e) => { e.stopPropagation(); handleDeleteChart(entry.id); }}
                              style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '18px',
                                color: colors.textMuted,
                                cursor: 'pointer',
                                padding: '8px',
                                minWidth: '40px',
                                minHeight: '40px',
                                lineHeight: 1,
                                fontFamily: 'inherit',
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.color = colors.textSecondary}
                              onMouseLeave={(e) => e.currentTarget.style.color = colors.textMuted}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <BirthChartForm onSubmit={handleChartCalculation} />
                </div>
              )}

              {birthChart && !loading && (
                <div>
                  {/* Chart switcher — jump between saved charts */}
                  {savedCharts.length > 0 && (
                    <div style={{ textAlign: 'center', marginBottom: '24px', position: 'relative', zIndex: 50 }}>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <button
                          onClick={() => setShowSwitcher((v) => !v)}
                          style={{
                            background: 'none',
                            border: `1px solid ${colors.borderGold}`,
                            borderRadius: '2px',
                            padding: '8px 16px',
                            fontSize: isMobile ? '13px' : '11px',
                            fontFamily: 'inherit',
                            fontWeight: '500',
                            letterSpacing: '1.5px',
                            textTransform: 'uppercase',
                            color: colors.gold,
                            cursor: 'pointer',
                          }}
                        >
                          {currentEntry ? currentEntry.label : 'Switch chart'} <span style={{ fontSize: '9px' }}>▾</span>
                        </button>
                        {showSwitcher && (
                          <>
                            <div
                              onClick={() => setShowSwitcher(false)}
                              style={{ position: 'fixed', inset: 0, zIndex: 40 }}
                            />
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              marginTop: '8px',
                              minWidth: '260px',
                              backgroundColor: '#ffffff',
                              border: `1px solid ${colors.borderSubtle}`,
                              boxShadow: '0 16px 48px rgba(26, 20, 16, 0.14)',
                              zIndex: 50,
                              textAlign: 'left',
                              overflow: 'hidden',
                            }}>
                              {savedCharts.map((entry) => {
                                const active = entry.id === currentKey;
                                return (
                                  <button
                                    key={entry.id}
                                    onClick={() => handleLoadChart(entry)}
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      width: '100%',
                                      gap: '12px',
                                      background: active ? colors.darkSurface : 'transparent',
                                      border: 'none',
                                      borderBottom: `1px solid ${colors.borderSubtle}`,
                                      padding: '13px 16px',
                                      cursor: 'pointer',
                                      fontFamily: 'inherit',
                                      textAlign: 'left',
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = colors.darkSurfaceHover}
                                    onMouseLeave={(e) => e.currentTarget.style.background = active ? colors.darkSurface : 'transparent'}
                                  >
                                    <span style={{
                                      fontFamily: "'Cormorant Garamond', Georgia, serif",
                                      fontSize: '16px',
                                      color: colors.textPrimary,
                                    }}>
                                      {active ? '✓ ' : ''}{entry.label}
                                    </span>
                                    <span style={{ fontSize: isMobile ? '13px' : '11px', color: colors.textMuted, whiteSpace: 'nowrap' }}>
                                      {entry.chart.birthData.date}
                                    </span>
                                  </button>
                                );
                              })}
                              <button
                                onClick={() => { setBirthChart(null); setShowSwitcher(false); window.scrollTo({ top: 0 }); }}
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  background: 'transparent',
                                  border: 'none',
                                  padding: '13px 16px',
                                  cursor: 'pointer',
                                  fontFamily: 'inherit',
                                  fontSize: isMobile ? '14px' : '12px',
                                  fontWeight: '500',
                                  letterSpacing: '1px',
                                  textTransform: 'uppercase',
                                  color: colors.gold,
                                  textAlign: 'left',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = colors.darkSurfaceHover}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                              >
                                + Calculate new chart
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  <ChartDisplay chart={birthChart} />

                  {/* Save this chart (local) */}
                  <div style={{ maxWidth: '640px', margin: '40px auto 0', padding: '0 24px', textAlign: 'center', minHeight: '44px' }}>
                    {currentSaved ? (
                      <span style={{ fontSize: isMobile ? '15px' : '13px', fontStyle: 'italic', color: colors.gold }}>
                        ✓ Saved to your charts
                      </span>
                    ) : showSaveInput ? (
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                        <input
                          type="text"
                          value={saveLabel}
                          onChange={(e) => setSaveLabel(e.target.value)}
                          placeholder={defaultLabel(birthChart)}
                          autoFocus
                          onKeyDown={(e) => { if (e.key === 'Enter') handleSaveChart(saveLabel); }}
                          style={{
                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                            fontSize: '16px',
                            fontWeight: '300',
                            padding: '10px 0',
                            border: 'none',
                            borderBottom: `1px solid ${colors.borderSubtle}`,
                            backgroundColor: 'transparent',
                            outline: 'none',
                            color: colors.textPrimary,
                            minWidth: '180px',
                          }}
                          onFocus={(e) => e.target.style.borderBottomColor = colors.gold}
                          onBlur={(e) => e.target.style.borderBottomColor = colors.borderSubtle}
                        />
                        <button
                          onClick={() => handleSaveChart(saveLabel)}
                          style={{
                            padding: '10px 22px',
                            fontSize: isMobile ? '13px' : '11px',
                            fontFamily: 'inherit',
                            fontWeight: '500',
                            letterSpacing: '2px',
                            textTransform: 'uppercase',
                            color: colors.darkBg,
                            backgroundColor: colors.gold,
                            border: 'none',
                            cursor: 'pointer',
                          }}
                        >
                          Save
                        </button>
                        <button
                          onClick={() => { setShowSaveInput(false); setSaveLabel(''); }}
                          style={{
                            background: 'none',
                            border: 'none',
                            fontSize: isMobile ? '14px' : '12px',
                            fontStyle: 'italic',
                            color: colors.textMuted,
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowSaveInput(true)}
                        style={{
                          padding: '13px 32px',
                          fontSize: isMobile ? '13px' : '11px',
                          fontFamily: 'inherit',
                          fontWeight: '500',
                          letterSpacing: '2px',
                          textTransform: 'uppercase',
                          color: colors.gold,
                          backgroundColor: 'transparent',
                          border: `1px solid ${colors.borderGold}`,
                          cursor: 'pointer',
                          transition: 'border-color 0.3s ease, color 0.3s ease',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = colors.gold; e.currentTarget.style.color = colors.goldBright; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = colors.borderGold; e.currentTarget.style.color = colors.gold; }}
                      >
                        Save this chart
                      </button>
                    )}
                  </div>

                  {/* What's next */}
                  <div style={{
                    maxWidth: '640px',
                    margin: '80px auto 0',
                    padding: '48px 24px',
                    borderTop: `1px solid ${colors.borderSubtle}`,
                    textAlign: 'center',
                  }}>
                    <p style={{
                      fontSize: isMobile ? '12px' : '10px',
                      fontWeight: '500',
                      letterSpacing: '2.5px',
                      textTransform: 'uppercase',
                      color: colors.gold,
                      marginBottom: '16px',
                    }}>
                      What's next
                    </p>
                    <h2 style={{
                      fontFamily: "'Cormorant Garamond', Georgia, serif",
                      fontSize: 'clamp(22px, 6vw, 32px)',
                      fontWeight: '400',
                      color: colors.textPrimary,
                      marginBottom: '12px',
                      lineHeight: '1.2',
                    }}>
                      Your chart is the map. The sky is what's moving.
                    </h2>
                    <p style={{
                      fontSize: isMobile ? '17px' : '15px',
                      fontWeight: '300',
                      fontStyle: 'italic',
                      color: colors.textSecondary,
                      lineHeight: '1.7',
                      marginBottom: '40px',
                    }}>
                      Come back daily to see which planets are activating your chart right now — and what the year ahead looks like.
                    </p>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '16px',
                      justifyContent: 'center',
                    }}>
                      <button
                        onClick={() => setActiveTab('calendar')}
                        style={{
                          padding: '16px 32px',
                          fontSize: isMobile ? '13px' : '11px',
                          fontFamily: 'inherit',
                          fontWeight: '500',
                          letterSpacing: '2.5px',
                          textTransform: 'uppercase',
                          color: colors.darkBg,
                          backgroundColor: colors.gold,
                          border: 'none',
                          cursor: 'pointer',
                          transition: 'background-color 0.3s ease',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.goldBright}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.gold}
                      >
                        Today's Sky
                      </button>
                      <button
                        onClick={() => setActiveTab('year-calendar')}
                        style={{
                          padding: '16px 32px',
                          fontSize: isMobile ? '13px' : '11px',
                          fontFamily: 'inherit',
                          fontWeight: '500',
                          letterSpacing: '2.5px',
                          textTransform: 'uppercase',
                          color: colors.gold,
                          backgroundColor: 'transparent',
                          border: `1px solid ${colors.borderGold}`,
                          cursor: 'pointer',
                          transition: 'border-color 0.3s ease, color 0.3s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = colors.gold;
                          e.currentTarget.style.color = colors.goldBright;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = colors.borderGold;
                          e.currentTarget.style.color = colors.gold;
                        }}
                      >
                        Year Ahead
                      </button>
                    </div>
                    <button
                      onClick={() => setBirthChart(null)}
                      style={{
                        marginTop: '32px',
                        background: 'none',
                        border: 'none',
                        fontSize: isMobile ? '14px' : '12px',
                        fontStyle: 'italic',
                        color: colors.textMuted,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        textDecoration: 'underline',
                      }}
                    >
                      Calculate a new chart
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'calendar' && (
            <TransitCalendar birthChart={birthChart} unknownTime={unknownTime} onNavigate={setActiveTab} />
          )}

          {activeTab === 'year-calendar' && (
            <TransitYearCalendar birthChart={birthChart} unknownTime={unknownTime} />
          )}

          {activeTab === 'compatibility' && (
            <Synastry person1={birthChart} savedCharts={savedCharts} onNavigate={setActiveTab} />
          )}

          {activeTab === 'privacy' && <PrivacyPolicy onNavigate={setActiveTab} />}
          {activeTab === 'disclaimer' && <Disclaimer onNavigate={setActiveTab} />}
          {activeTab === 'terms' && <TermsOfUse onNavigate={setActiveTab} />}
          </Suspense>
      </main>

      <Footer onNavigate={setActiveTab} />

      {/* Sidebar Navigation Overlay */}
      <Sidebar
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
    </div>
  );
}

export default App;
